import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Kommentar, KommentarEntityTyp } from '../types';
import kommentarService from '../services/kommentarService';
import authService from '../services/authService';
import { Card, Button, Textarea, Spinner, Icon } from './ui';
import { ankunft, markiereNeu } from '../utils/ankunft';

// ISO-Zeitstempel -> "24.06.2026, 14:30".
const fmtZeit = (iso?: string): string => {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const initialen = (name: string): string =>
  name.split(' ').filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?';

/**
 * Kommentar-Sektion für Detailseiten (Grab/Kunde): chronologische Anzeige mit Name + Datum,
 * jeder Mitarbeiter kann kommentieren, eigene Kommentare lassen sich bearbeiten (mit „(bearbeitet)“-
 * Kennzeichnung) und löschen.
 */
const KommentarSektion: React.FC<{ entityType: KommentarEntityTyp; entityId: number }> = ({ entityType, entityId }) => {
  const currentUserId = authService.getCurrentUser()?.id;

  const [kommentare, setKommentare] = useState<Kommentar[]>([]);
  const [loading, setLoading] = useState(true);
  const [neu, setNeu] = useState('');
  const [posting, setPosting] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setKommentare(await kommentarService.getForEntity(entityType, entityId));
    } catch {
      /* leere Liste anzeigen */
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const submitNeu = async (e: React.FormEvent) => {
    e.preventDefault();
    const inhalt = neu.trim();
    if (!inhalt) return;
    setPosting(true);
    try {
      const neuer = await kommentarService.create(entityType, entityId, { inhalt });
      markiereNeu(`kommentar-${neuer.id}`);
      setNeu('');
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Kommentar konnte nicht gespeichert werden');
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (k: Kommentar) => { setEditId(k.id); setEditText(k.inhalt); };
  const cancelEdit = () => { setEditId(null); setEditText(''); };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    const inhalt = editText.trim();
    if (!inhalt || editId == null) return;
    setSavingEdit(true);
    try {
      await kommentarService.update(editId, { inhalt });
      markiereNeu(`kommentar-${editId}`);
      cancelEdit();
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Kommentar konnte nicht gespeichert werden');
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (k: Kommentar) => {
    if (!window.confirm('Kommentar löschen?')) return;
    try {
      await kommentarService.delete(k.id);
      toast.success('Kommentar gelöscht');
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Kommentar konnte nicht gelöscht werden');
    }
  };

  return (
    <Card className="p-6">
      <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
        <span className="text-gray-400"><Icon.Chat className="h-4 w-4" /></span>
        Kommentare{kommentare.length > 0 ? ` (${kommentare.length})` : ''}
      </h3>

      {/* Neuer Kommentar */}
      <form onSubmit={submitNeu} className="mb-6">
        <Textarea
          rows={3}
          value={neu}
          onChange={e => setNeu(e.target.value)}
          placeholder="Kommentar schreiben…"
        />
        <div className="mt-2 flex justify-end">
          <Button type="submit" loading={posting} disabled={!neu.trim()} icon={<Icon.Chat className="h-4 w-4" />}>
            Kommentieren
          </Button>
        </div>
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner className="h-4 w-4 text-brand-600" /> Lade Kommentare…</div>
      ) : kommentare.length === 0 ? (
        <p className="text-sm text-gray-400">Noch keine Kommentare. Absprachen und Hinweise für Kollegen gehören hierher.</p>
      ) : (
        <ul className="space-y-4">
          {kommentare.map(k => {
            const eigener = currentUserId != null && k.userId === currentUserId;
            const editing = editId === k.id;
            const a = ankunft(`kommentar-${k.id}`);
            return (
              <li key={k.id} ref={a.ref} className={`flex gap-3 rounded-lg ${a.className}`}>
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                  {initialen(k.autorName)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-semibold text-gray-900">{k.autorName}</span>
                    <span className="text-xs text-gray-400">{fmtZeit(k.createdAt)}</span>
                    {k.bearbeitet && <span className="text-xs italic text-gray-400">(bearbeitet)</span>}
                  </div>

                  {editing ? (
                    <form onSubmit={saveEdit} className="ui-aufklappen mt-2">
                      <Textarea rows={3} value={editText} onChange={e => setEditText(e.target.value)} autoFocus />
                      <div className="mt-2 flex justify-end gap-2">
                        <Button type="button" variant="secondary" size="sm" onClick={cancelEdit}>Abbrechen</Button>
                        <Button type="submit" size="sm" loading={savingEdit} disabled={!editText.trim()}>Speichern</Button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-700">{k.inhalt}</p>
                      {eigener && (
                        <div className="mt-1 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => startEdit(k)}
                            className="rounded-md px-2 py-0.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-50"
                          >
                            Bearbeiten
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(k)}
                            className="rounded-md px-2 py-0.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                          >
                            Löschen
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

export default KommentarSektion;
